import { Icon } from "@uigovpe/components";
import { useFormContext } from "react-hook-form";
import type { CategoryOption, ProfileFormData } from "../../../types";
import { formatCep, formatCnpj, formatCpf, formatPhone } from "@/utils/formats";

const optional = (value?: string | null) => value?.trim() || "Não informado";
const yesNo = (value: boolean) => (value ? "Sim" : "Não");
const locationPreferenceLabels: Record<
  ProfileFormData["locationDisplayPreference"],
  string
> = {
  neighborhood_city: "Bairro e município",
  full_address: "Endereço completo",
  online_or_on_request: "Online ou sob consulta",
};

function ReviewItem({
  label,
  value,
  className = "",
}: {
  label: string;
  value?: string | null;
  className?: string;
}) {
  return (
    <div className={`flex! min-w-0! flex-col! gap-1! ${className}`}>
      <dt className="text-xs! font-bold! text-zinc-700!">{label}</dt>
      <dd className="m-0! break-words! text-sm! text-zinc-700!">
        {optional(value)}
      </dd>
    </div>
  );
}

function ReviewSection({
  icon,
  title,
  children,
}: {
  icon: React.ComponentProps<typeof Icon>["icon"];
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex! flex-col! gap-4!">
      <div className="flex! items-center! gap-2! text-base! font-bold! text-zinc-950!">
        <Icon icon={icon} outline className="text-primary!" />
        <h3 className="m-0! text-base! font-bold!">{title}</h3>
      </div>
      {children}
    </section>
  );
}

export default function ProfileStepReview({
  categories,
}: {
  categories: CategoryOption[];
}) {
  const { watch } = useFormContext<ProfileFormData>();
  const data = watch();
  const location =
    data.locationDisplayPreference === "full_address"
      ? [
          data.address,
          data.addressNumber,
          data.neighborhood,
          `${data.city} - PE`,
        ]
          .filter(Boolean)
          .join(", ")
      : data.locationDisplayPreference === "online_or_on_request"
        ? `${data.city} - PE. Endereço sob consulta.`
        : `${data.neighborhood}, ${data.city} - PE`;

  return (
    <div className="flex! w-full! flex-col! gap-8!">
      <div className="flex! flex-col! gap-2!">
        <h2 className="m-0! text-lg! font-bold! text-zinc-950!">
          Revise os dados da sua vitrine antes de enviar.
        </h2>
        <p className="m-0! text-sm! text-zinc-700!">
          Confira suas informações. Se precisar corrigir algo, volte para a
          etapa correspondente.
        </p>
      </div>
      <div className="flex! flex-col! gap-10!">
        <ReviewSection icon="person" title="Identificação">
          <dl className="grid! grid-cols-1! gap-x-8! gap-y-5! md:grid-cols-2!">
            <ReviewItem label="Nome completo" value={data.fullName} />
            <ReviewItem label="E-mail" value={data.email} />
            <ReviewItem label="CPF" value={formatCpf(data.cpf)} />
            <ReviewItem label="Celular" value={formatPhone(data.phone)} />
          </dl>
        </ReviewSection>
        <ReviewSection
          icon="volunteer_activism"
          title="Informações complementares"
        >
          <dl className="grid! grid-cols-1! gap-x-8! gap-y-5! md:grid-cols-3!">
            <ReviewItem label="PCD" value={yesNo(data.isDisabledPerson)} />
            <ReviewItem
              label="Tipo de deficiência"
              value={
                data.isDisabledPerson ? data.disabilityType : "Não se aplica"
              }
            />
            <ReviewItem
              label="Mãe atípica"
              value={yesNo(data.isAtypicalMother)}
            />
          </dl>
        </ReviewSection>
        <ReviewSection icon="business_center" title="Sobre seu negócio">
          <dl className="grid! grid-cols-1! gap-x-8! gap-y-5! md:grid-cols-3!">
            <ReviewItem label="Nome do negócio" value={data.businessName} />
            <ReviewItem label="CNPJ" value={formatCnpj(data.cnpj)} />
            <ReviewItem label="Website" value={data.website} />
            <ReviewItem
              label="Categoria"
              value={
                categories.find((item) => item.value === data.businessCategory)
                  ?.label
              }
            />
            <ReviewItem
              label="Associação ou cooperativa"
              value={yesNo(data.participatesInAssociation)}
            />
            <ReviewItem
              label="Nome da associação"
              value={
                data.participatesInAssociation
                  ? data.associationName
                  : "Não se aplica"
              }
            />
            <ReviewItem
              label="Descrição do negócio"
              value={data.businessDescription}
              className="md:col-span-3!"
            />
          </dl>
        </ReviewSection>
        <ReviewSection icon="share" title="Redes sociais">
          <dl className="grid! grid-cols-1! gap-x-8! gap-y-5! md:grid-cols-3!">
            <ReviewItem label="Instagram" value={data.instagram} />
            <ReviewItem label="Facebook" value={data.facebook} />
            <ReviewItem label="TikTok" value={data.tiktok} />
          </dl>
        </ReviewSection>
        <ReviewSection icon="location_on" title="Localização do negócio">
          <dl className="grid! grid-cols-1! gap-x-8! gap-y-5! md:grid-cols-3!">
            <ReviewItem label="CEP" value={formatCep(data.zipCode)} />
            <ReviewItem
              label="Endereço"
              value={`${data.address}, ${data.addressNumber}`}
            />
            <ReviewItem label="Bairro" value={data.neighborhood} />
            <ReviewItem label="Município" value={`${data.city} - PE`} />
            <ReviewItem
              label="Preferência de exibição"
              value={locationPreferenceLabels[data.locationDisplayPreference]}
            />
            <ReviewItem label="Na vitrine será exibido" value={location} />
          </dl>
        </ReviewSection>
      </div>
    </div>
  );
}
